import { useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Save } from "lucide-react";

const SKILLS = ["Engineering", "Product", "Design", "Sales", "Marketing", "Finance", "Operations", "AI/ML", "Data", "Growth", "Legal", "HR"];
const INDUSTRIES = ["AI/ML", "Fintech", "Healthcare", "SaaS", "Climate", "Deep Tech", "Consumer", "Enterprise", "Web3", "Biotech"];
const OPEN_TO = ["remote", "hybrid", "onsite"];

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [f, setF] = useState({
    full_name: user?.full_name || "",
    headline: user?.headline || "",
    bio: user?.bio || "",
    country: user?.country || "",
    city: user?.city || "",
    current_role: user?.current_role || "",
    current_org: user?.current_org || "",
    experience_years: user?.experience_years || 0,
    linkedin: user?.linkedin || "",
    github: user?.github || "",
    website: user?.website || "",
    skills: user?.skills || [],
    industries: user?.industries || [],
    open_to: user?.open_to || [],
    mission: user?.mission || "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f, experience_years: parseInt(f.experience_years || 0) };
      const { data } = await api.patch("/profile", payload);
      updateUser(data);
      toast.success("Profile saved");
    } catch { toast.error("Failed to save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-caps mb-1">Your profile</div>
          <h1 className="text-3xl tracking-tight font-semibold">Edit profile</h1>
        </div>
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="profile-save">
          <Save className="h-4 w-4 mr-1" />{busy ? "Saving…" : "Save"}
        </Button>
      </div>

      <Card className="p-6 md:p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Full name</Label><Input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="pf-name" className="mt-1.5" /></div>
          <div><Label>Headline</Label><Input value={f.headline} onChange={(e) => set("headline", e.target.value)} data-testid="pf-headline" className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Bio</Label><Textarea rows={4} value={f.bio} onChange={(e) => set("bio", e.target.value)} data-testid="pf-bio" className="mt-1.5" /></div>
          <div><Label>Country</Label><Input value={f.country} onChange={(e) => set("country", e.target.value)} data-testid="pf-country" className="mt-1.5" /></div>
          <div><Label>City</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="pf-city" className="mt-1.5" /></div>
          <div><Label>Current role</Label><Input value={f.current_role} onChange={(e) => set("current_role", e.target.value)} data-testid="pf-role" className="mt-1.5" /></div>
          <div><Label>Current org</Label><Input value={f.current_org} onChange={(e) => set("current_org", e.target.value)} data-testid="pf-org" className="mt-1.5" /></div>
          <div><Label>Experience (years)</Label><Input type="number" value={f.experience_years} onChange={(e) => set("experience_years", e.target.value)} data-testid="pf-exp" className="mt-1.5" /></div>
          <div><Label>LinkedIn</Label><Input value={f.linkedin} onChange={(e) => set("linkedin", e.target.value)} data-testid="pf-linkedin" className="mt-1.5" /></div>
          <div><Label>GitHub</Label><Input value={f.github} onChange={(e) => set("github", e.target.value)} data-testid="pf-github" className="mt-1.5" /></div>
          <div><Label>Website</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="pf-website" className="mt-1.5" /></div>
        </div>

        <div>
          <Label className="mb-2 block">Skills</Label>
          <div className="flex flex-wrap gap-2" data-testid="pf-skills">
            {SKILLS.map((s) => (
              <button key={s} onClick={() => toggle("skills", s)} type="button"
                className={`px-3 py-1 rounded-full text-xs border transition-colors duration-200 ${
                  f.skills.includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="mb-2 block">Industries</Label>
          <div className="flex flex-wrap gap-2" data-testid="pf-industries">
            {INDUSTRIES.map((s) => (
              <button key={s} onClick={() => toggle("industries", s)} type="button"
                className={`px-3 py-1 rounded-full text-xs border transition-colors duration-200 ${
                  f.industries.includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="mb-2 block">Open to</Label>
          <div className="flex flex-wrap gap-2" data-testid="pf-opento">
            {OPEN_TO.map((s) => (
              <button key={s} onClick={() => toggle("open_to", s)} type="button"
                className={`px-3 py-1 rounded-full text-xs border transition-colors duration-200 ${
                  f.open_to.includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>Mission</Label>
          <Textarea rows={3} value={f.mission} onChange={(e) => set("mission", e.target.value)} data-testid="pf-mission" className="mt-1.5" />
        </div>
      </Card>
    </div>
  );
}
